import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Send, Paperclip, Image as ImageIcon, Smile,
    MoreVertical, Check, CheckCheck, Clock, ArrowLeft
} from 'lucide-react'

interface Message {
    id: string
    text: string
    sender: 'student' | 'mentor'
    timestamp: string
    status: 'sent' | 'delivered' | 'read'
}

interface Mentor {
    id: string
    name: string
    subject: string
    avatar: string
    online: boolean
    lastSeen?: string
}

export function ChatView() {
    const [selectedMentor, setSelectedMentor] = useState<Mentor | null>(null)
    const [messageText, setMessageText] = useState('')
    const [messages, setMessages] = useState<Message[]>([])
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const mentors: Mentor[] = [
        { id: '1', name: 'Dr. Sarah Patel', subject: 'Physics', avatar: 'SP', online: true },
        { id: '2', name: 'Prof. Rajesh Kumar', subject: 'Chemistry', avatar: 'RK', online: false, lastSeen: '2h ago' },
        { id: '3', name: 'Dr. Meera Singh', subject: 'Biology', avatar: 'MS', online: true },
    ]

    const mockConversations: Record<string, Message[]> = {
        '1': [
            { id: '1', text: 'Hi Dr. Sarah, I have a doubt in Thermodynamics.', sender: 'student', timestamp: '10:30 AM', status: 'read' },
            { id: '2', text: 'Sure! What topic are you struggling with?', sender: 'mentor', timestamp: '10:32 AM', status: 'read' },
            { id: '3', text: 'I\'m confused about the second law and entropy calculation.', sender: 'student', timestamp: '10:33 AM', status: 'read' },
            { id: '4', text: 'Great question! Let me explain. The second law states that entropy of an isolated system always increases...', sender: 'mentor', timestamp: '10:35 AM', status: 'read' },
            { id: '5', text: 'For entropy calculation, remember ΔS = Q/T for reversible processes.', sender: 'mentor', timestamp: '10:35 AM', status: 'read' },
            { id: '6', text: 'That makes sense! Thank you so much!', sender: 'student', timestamp: '10:38 AM', status: 'read' },
        ],
        '2': [
            { id: '1', text: 'Sir, can you help with organic reaction mechanisms?', sender: 'student', timestamp: 'Yesterday', status: 'read' },
            { id: '2', text: 'Of course! Which reaction are you working on?', sender: 'mentor', timestamp: 'Yesterday', status: 'read' },
        ],
        '3': [
            { id: '1', text: 'Dr. Meera, I need help with Human Physiology diagrams.', sender: 'student', timestamp: '9:15 AM', status: 'delivered' },
        ],
    }

    useEffect(() => {
        if (selectedMentor) {
            setMessages(mockConversations[selectedMentor.id] || [])
        }
    }, [selectedMentor])

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    const handleSend = () => {
        if (!messageText.trim() || !selectedMentor) return

        const newMessage: Message = {
            id: Date.now().toString(),
            text: messageText,
            sender: 'student',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            status: 'sent'
        }

        setMessages([...messages, newMessage])
        setMessageText('')
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    // Mobile: Show mentor list or chat
    const [showMentorList, setShowMentorList] = useState(true)

    const handleSelectMentor = (mentor: Mentor) => {
        setSelectedMentor(mentor)
        setShowMentorList(false)
    }

    return (
        <div className="h-[calc(100dvh-210px)] lg:h-[calc(100vh-100px)] flex pb-0 lg:pb-0">
            <GlassmorphicCard className="flex-1 flex overflow-hidden">
                {/* Mentor List - Desktop always visible, Mobile conditional */}
                <div className={`${showMentorList ? 'flex' : 'hidden lg:flex'} flex-col w-full lg:w-72 border-r border-slate-100 dark:border-slate-800`}>
                    <div className="p-4 border-b border-slate-100 dark:border-slate-800">
                        <h3 className="font-bold text-theme-primary">Chat with Mentors</h3>
                        <p className="text-xs text-theme-secondary mt-0.5">Ask doubts & get help</p>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {mentors.map((mentor) => (
                            <button
                                key={mentor.id}
                                onClick={() => handleSelectMentor(mentor)}
                                className={`w-full flex items-center gap-3 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50 ${selectedMentor?.id === mentor.id ? 'bg-slate-50 dark:bg-slate-800/50' : ''}`}
                            >
                                <div className="relative">
                                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                                        {mentor.avatar}
                                    </div>
                                    {mentor.online && (
                                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-slate-900" />
                                    )}
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                    <p className="font-medium text-theme-primary text-sm truncate">{mentor.name}</p>
                                    <p className="text-xs text-theme-secondary truncate">
                                        {mentor.subject} • {mentor.online ? 'Online' : mentor.lastSeen}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Chat Area */}
                <div className={`${!showMentorList ? 'flex' : 'hidden lg:flex'} flex-col flex-1`}>
                    {selectedMentor ? (
                        <>
                            {/* Chat Header */}
                            <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3">
                                    {/* Back button for mobile */}
                                    <button
                                        onClick={() => setShowMentorList(true)}
                                        className="lg:hidden p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <ArrowLeft className="h-5 w-5 text-slate-500" />
                                    </button>
                                    <div className="relative">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                                            {selectedMentor.avatar}
                                        </div>
                                        {selectedMentor.online && (
                                            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-slate-900" />
                                        )}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-theme-primary text-sm">{selectedMentor.name}</p>
                                        <p className="text-xs text-green-500">{selectedMentor.online ? 'Online' : selectedMentor.lastSeen}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                                        <MoreVertical className="h-4.5 w-4.5" />
                                    </button>
                                </div>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                {messages.map((msg) => (
                                    <motion.div
                                        key={msg.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex ${msg.sender === 'student' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div className={`max-w-[80%] md:max-w-[70%] p-3 rounded-2xl ${msg.sender === 'student'
                                            ? 'bg-primary-blue text-white rounded-br-md'
                                            : 'bg-slate-100 dark:bg-slate-800 text-theme-primary rounded-bl-md'
                                            }`}>
                                            <p className="text-sm leading-relaxed">{msg.text}</p>
                                            <div className={`flex items-center justify-end gap-1 mt-1 ${msg.sender === 'student' ? 'text-blue-100' : 'text-slate-400'}`}>
                                                <span className="text-[10px]">{msg.timestamp}</span>
                                                {msg.sender === 'student' && (
                                                    msg.status === 'read' ? <CheckCheck className="h-3 w-3" /> :
                                                        msg.status === 'delivered' ? <Check className="h-3 w-3" /> :
                                                            <Clock className="h-3 w-3" />
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message Input */}
                            <div className="p-4 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-end gap-2">
                                    <div className="flex gap-1">
                                        <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400">
                                            <Paperclip className="h-5 w-5" />
                                        </button>
                                        <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hidden sm:block">
                                            <ImageIcon className="h-5 w-5" />
                                        </button>
                                    </div>
                                    <div className="flex-1 relative">
                                        <textarea
                                            rows={1}
                                            value={messageText}
                                            onChange={(e) => setMessageText(e.target.value)}
                                            onKeyDown={handleKeyPress}
                                            placeholder="Type a message..."
                                            className="w-full px-4 py-2.5 pr-10 bg-slate-100 dark:bg-slate-800 rounded-xl resize-none text-sm text-theme-primary placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-blue/20"
                                        />
                                        <button className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                                            <Smile className="h-5 w-5" />
                                        </button>
                                    </div>
                                    <button
                                        onClick={handleSend}
                                        disabled={!messageText.trim()}
                                        className="p-2.5 bg-primary-blue text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <Send className="h-5 w-5" />
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                                    <Send className="h-8 w-8 text-slate-300" />
                                </div>
                                <p className="text-theme-secondary font-medium">Select a mentor to start chatting</p>
                                <p className="text-sm text-slate-400 mt-1">Get help with your doubts</p>
                            </div>
                        </div>
                    )}
                </div>
            </GlassmorphicCard>
        </div>
    )
}
